import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { chatAPI } from '../services/api';
import {
  MessageCircle, X, Send, ArrowLeft, Search, Loader2, Check, CheckCheck, Users, Shield
} from 'lucide-react';

const ChatBubble = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [view, setView] = useState('list'); // list | chat | new
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [chatUsers, setChatUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  // Check if chat is enabled
  useEffect(() => {
    if (!user) return;
    chatAPI.getStatus().then(r => setEnabled(r.data?.enabled !== false)).catch(() => {});
  }, [user]);

  // Poll unread count
  useEffect(() => {
    if (!user || !enabled) return;
    const poll = () => chatAPI.getUnreadCount().then(r => setUnreadCount(r.data?.count || 0)).catch(() => {});
    poll();
    const interval = setInterval(poll, 8000);
    return () => clearInterval(interval);
  }, [user, enabled]);

  // Load conversations when panel opens
  useEffect(() => {
    if (!open || !enabled) return;
    setLoading(true);
    chatAPI.getConversations()
      .then(r => setConversations(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, enabled]);

  // Poll messages in active conversation
  useEffect(() => {
    if (!activeConv || view !== 'chat') {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    const loadMsgs = () => {
      chatAPI.getMessages(activeConv.id).then(r => setMessages(r.data || [])).catch(() => {});
    };
    loadMsgs();
    chatAPI.markRead(activeConv.id).then(() => {
      setUnreadCount(prev => Math.max(0, prev - (activeConv.unread_count || 0)));
      setConversations(prev => prev.map(c => c.id === activeConv.id ? { ...c, unread_count: 0 } : c));
    }).catch(() => {});
    pollRef.current = setInterval(loadMsgs, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeConv, view]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const openConversation = (conv) => {
    setActiveConv(conv);
    setView('chat');
    setNewMessage('');
  };

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      await chatAPI.sendMessage(activeConv.id, newMessage.trim());
      setNewMessage('');
      const r = await chatAPI.getMessages(activeConv.id);
      setMessages(r.data || []);
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const openNewChat = async () => {
    setView('new');
    setUserSearch('');
    try {
      const r = await chatAPI.getUsers();
      setChatUsers(r.data || []);
    } catch (err) { console.error(err); }
  };

  const startNewConversation = async (targetUser) => {
    // Check if conversation already exists
    const existing = conversations.find(c => c.other_user?.id === targetUser.id);
    if (existing) {
      openConversation(existing);
      return;
    }
    setView('chat');
    setActiveConv({ id: null, other_user: targetUser, new: true });
    setMessages([]);
  };

  const handleSendFirst = async () => {
    if (!newMessage.trim() || sending || !activeConv?.other_user) return;
    setSending(true);
    try {
      const r = await chatAPI.createConversation(activeConv.other_user.id, newMessage.trim());
      setNewMessage('');
      const convId = r.data.conversation_id;
      setActiveConv({ ...activeConv, id: convId, new: false });
      const msgs = await chatAPI.getMessages(convId);
      setMessages(msgs.data || []);
      const convs = await chatAPI.getConversations();
      setConversations(convs.data || []);
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  const goBack = () => {
    setView('list');
    setActiveConv(null);
    chatAPI.getConversations().then(r => setConversations(r.data || [])).catch(() => {});
  };

  const filteredChatUsers = chatUsers.filter(u =>
    !userSearch || u.name?.toLowerCase().includes(userSearch.toLowerCase()) || u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  if (!user || !enabled) return null;

  return (
    <>
      {/* Bubble Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-[#EC2229] text-white shadow-lg shadow-[#EC2229]/30 flex items-center justify-center hover:scale-110 transition-transform z-50"
          data-testid="chat-bubble-btn"
        >
          <MessageCircle className="w-6 h-6" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-white text-[#EC2229] text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="chat-unread-badge">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div className="fixed bottom-6 right-6 w-[380px] h-[520px] bg-[#1A1D23] border border-white/10 rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden" data-testid="chat-panel">
          {/* Header */}
          <div className="bg-[#EC2229] px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              {view !== 'list' && (
                <button onClick={goBack} className="text-white/80 hover:text-white mr-1" data-testid="chat-back-btn">
                  <ArrowLeft className="w-4 h-4" />
                </button>
              )}
              <MessageCircle className="w-5 h-5 text-white" />
              <span className="text-white font-semibold text-sm">
                {view === 'list' ? 'Nachrichten' : view === 'new' ? 'Neuer Chat' : activeConv?.other_user?.name || 'Chat'}
              </span>
              {view === 'chat' && activeConv?.other_user?.role === 'admin' && (
                <Shield className="w-3 h-3 text-white/60" />
              )}
            </div>
            <button onClick={() => { setOpen(false); setView('list'); }} className="text-white/80 hover:text-white" data-testid="chat-close-btn">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          {view === 'list' && (
            <div className="flex-1 overflow-y-auto">
              {/* New Chat Button */}
              <button onClick={openNewChat} className="w-full p-3 border-b border-white/[0.06] flex items-center gap-3 text-[#EC2229] hover:bg-white/5 transition-colors" data-testid="new-chat-btn">
                <div className="w-9 h-9 rounded-full bg-[#EC2229]/10 flex items-center justify-center">
                  <Users className="w-4 h-4" />
                </div>
                <span className="text-sm font-medium">Neuer Chat</span>
              </button>

              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#EC2229]" /></div>
              ) : conversations.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">Keine Unterhaltungen</p>
              ) : (
                conversations.map(conv => (
                  <button
                    key={conv.id}
                    onClick={() => openConversation(conv)}
                    className="w-full p-3 border-b border-white/[0.06] flex items-center gap-3 hover:bg-white/5 transition-colors text-left"
                    data-testid={`conv-${conv.id}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${conv.other_user?.role === 'admin' ? 'bg-[#EC2229]/20' : 'bg-blue-500/20'}`}>
                      <span className={`text-sm font-bold ${conv.other_user?.role === 'admin' ? 'text-[#EC2229]' : 'text-blue-400'}`}>
                        {conv.other_user?.name?.[0]?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-white font-medium truncate">{conv.other_user?.name || 'Unbekannt'}</span>
                        {conv.last_message_at && (
                          <span className="text-[10px] text-slate-600 flex-shrink-0 ml-2">
                            {new Date(conv.last_message_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-slate-500 truncate">{conv.last_message || ''}</p>
                        {conv.unread_count > 0 && (
                          <span className="ml-2 w-5 h-5 bg-[#EC2229] text-white text-[10px] rounded-full flex items-center justify-center flex-shrink-0">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}

          {view === 'new' && (
            <div className="flex-1 overflow-y-auto">
              <div className="p-3">
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                  <input
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    placeholder="Benutzer suchen..."
                    className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#EC2229]/50"
                    data-testid="chat-user-search"
                  />
                </div>
              </div>
              {filteredChatUsers.map(u => (
                <button
                  key={u.id}
                  onClick={() => startNewConversation(u)}
                  className="w-full p-3 border-b border-white/[0.06] flex items-center gap-3 hover:bg-white/5 transition-colors text-left"
                  data-testid={`chat-user-${u.id}`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center ${u.role === 'admin' ? 'bg-[#EC2229]/20' : 'bg-blue-500/20'}`}>
                    <span className={`text-sm font-bold ${u.role === 'admin' ? 'text-[#EC2229]' : 'text-blue-400'}`}>{u.name?.[0]?.toUpperCase()}</span>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm text-white font-medium truncate">{u.name}</span>
                      {u.role === 'admin' && <Shield className="w-3 h-3 text-[#EC2229]" />}
                    </div>
                    <span className="text-xs text-slate-500 truncate">{u.email}</span>
                  </div>
                </button>
              ))}
              {filteredChatUsers.length === 0 && <p className="text-sm text-slate-500 text-center py-8">Keine Benutzer gefunden</p>}
            </div>
          )}

          {view === 'chat' && (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="chat-messages">
                {messages.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-8">
                    {activeConv?.new ? 'Schreiben Sie die erste Nachricht' : 'Keine Nachrichten'}
                  </p>
                )}
                {messages.map(msg => {
                  const isMe = msg.sender_id === user?.id;
                  return (
                    <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${isMe ? 'bg-[#EC2229] text-white rounded-br-md' : 'bg-white/[0.08] text-slate-200 rounded-bl-md'}`}>
                        {!isMe && <p className="text-[10px] font-medium text-slate-400 mb-0.5">{msg.sender_name}</p>}
                        <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                        <div className={`flex items-center gap-1 mt-0.5 ${isMe ? 'justify-end' : ''}`}>
                          <span className={`text-[9px] ${isMe ? 'text-white/50' : 'text-slate-600'}`}>
                            {new Date(msg.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          {isMe && <CheckCheck className="w-3 h-3 text-white/50" />}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-3 border-t border-white/[0.06] flex-shrink-0">
                <div className="flex gap-2 items-end">
                  <textarea
                    value={newMessage}
                    onChange={e => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Nachricht..."
                    rows={1}
                    className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 resize-none focus:outline-none focus:border-[#EC2229]/50 max-h-20"
                    data-testid="chat-message-input"
                  />
                  <button
                    onClick={activeConv?.new ? handleSendFirst : handleSend}
                    disabled={!newMessage.trim() || sending}
                    className="w-9 h-9 rounded-xl bg-[#EC2229] text-white flex items-center justify-center hover:bg-[#EC2229]/90 disabled:opacity-40 transition-all flex-shrink-0"
                    data-testid="chat-send-btn"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ChatBubble;
